import { apiRequest, extractList, clearToken, setToken } from "./client";

const makeFilterParams = (filters = {}) => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, value);
    }
  });

  const query = params.toString();
  return query ? `?${query}` : "";
};

export const login = async (username, password) => {
  const response = await apiRequest("/api/v1/auth/login", {
    method: "POST",
    body: { username, password },
  });

  const token = response?.data?.token;
  if (token) {
    setToken(token);
  }

  return response?.data;
};

export const logout = async () => {
  try {
    await apiRequest("/api/v1/auth/logout", { method: "POST" });
  } finally {
    clearToken();
  }
};

export const getCurrentUser = async () => {
  const response = await apiRequest("/api/v1/auth/me");
  return response?.data;
};

export const getDashboard = async (filters = {}) => {
  const response = await apiRequest(`/api/v1/dashboard${makeFilterParams(filters)}`);
  return response?.data || {};
};

export const getNews = async (filters = {}) => {
  const response = await apiRequest(`/api/v1/news${makeFilterParams(filters)}`);
  return response?.data;
};

export const archiveNews = async (id) => {
  const response = await apiRequest(`/api/v1/news/${id}/archive`, { method: "PATCH" });
  return response?.data;
};

export const restoreNews = async (id) => {
  const response = await apiRequest(`/api/v1/news/${id}/restore`, { method: "PATCH" });
  return response?.data;
};

export const getLeadFilterOptions = async () => {
  const response = await apiRequest("/api/v1/leads/filter-options");
  return response?.data || { sources: [] };
};

export const getSearchOverview = async () => {
  const response = await apiRequest("/api/v1/search/overview");
  return response?.data || {};
};

export const getMediaCatalog = async () => {
  const response = await apiRequest("/api/v1/search/media-catalog");
  return response?.data || [];
};

export const getSources = async (filters = {}) => {
  const response = await apiRequest(`/api/v1/search/sources${makeFilterParams(filters)}`);
  return response?.data;
};

export const createSource = async (payload) => {
  const response = await apiRequest("/api/v1/search/sources", {
    method: "POST",
    body: payload,
  });
  return response?.data;
};

export const updateSource = async (id, payload) => {
  const response = await apiRequest(`/api/v1/search/sources/${id}`, {
    method: "PUT",
    body: payload,
  });
  return response?.data;
};

export const deleteSource = async (id) => {
  return apiRequest(`/api/v1/search/sources/${id}`, { method: "DELETE" });
};

export const runSourceNow = async (id) => {
  const response = await apiRequest(`/api/v1/search/sources/${id}/crawl-now`, {
    method: "POST",
  });
  return response?.data;
};

export const pauseSource = async (id) => {
  const response = await apiRequest(`/api/v1/search/sources/${id}/pause`, {
    method: "POST",
  });
  return response?.data;
};

export const resumeSource = async (id) => {
  const response = await apiRequest(`/api/v1/search/sources/${id}/resume`, {
    method: "POST",
  });
  return response?.data;
};

export const getStaff = async (filters = {}) => {
  const response = await apiRequest(`/api/v1/staff${makeFilterParams(filters)}`);
  return response?.data;
};

export const getRoles = async () => {
  const response = await apiRequest("/api/v1/staff/roles");
  return response?.data;
};

export const getPermissions = async () => {
  const response = await apiRequest("/api/v1/permissions");
  return response?.data;
};

export const getAiOverview = async () => {
  const response = await apiRequest("/api/v1/ai/overview");
  return response?.data || {};
};

export const getAiCredential = async () => {
  const response = await apiRequest("/api/v1/ai/credential");
  return response?.data || {};
};

export const updateAiCredential = async (payload) => {
  const response = await apiRequest("/api/v1/ai/credential", {
    method: "PUT",
    body: payload,
  });
  return response?.data;
};

export const testAiCredential = async () => {
  const response = await apiRequest("/api/v1/ai/credential/test", { method: "POST" });
  return response?.data;
};

export const getAiPrompts = async () => {
  const response = await apiRequest("/api/v1/ai/prompts");
  return extractList(response?.data);
};

export const updateAiPrompt = async (key, payload) => {
  const response = await apiRequest(`/api/v1/ai/prompts/${key}`, {
    method: "PUT",
    body: payload,
  });
  return response?.data;
};

export const testAiPrompt = async (key, payload = {}) => {
  const response = await apiRequest(`/api/v1/ai/prompts/${key}/test`, {
    method: "POST",
    body: { payload },
  });
  return response?.data;
};

export const getSettings = async () => {
  const response = await apiRequest("/api/v1/settings");
  return response?.data || {};
};

export const updateSettings = async (payload) => {
  const response = await apiRequest("/api/v1/settings", {
    method: "PUT",
    body: payload,
  });
  return response?.data;
};

export const syncMyBinaProjects = async (months = 3) => {
  const response = await apiRequest("/api/v1/mybina/projects/sync", {
    method: "POST",
    body: { months },
  });
  return response;
};

export const getLeadReport = async (filters = {}) => {
  const response = await apiRequest(`/api/v1/reports/leads${makeFilterParams(filters)}`);
  return response?.data || {};
};

export const getAuditLogs = async (filters = {}) => {
  const response = await apiRequest(`/api/v1/audit-logs${makeFilterParams(filters)}`);
  return response || {};
};
